import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'antd';
import {
  Home,
  Calendar,
  Clock,
  Users,
  Menu as MenuIcon,
  Heart,
  Inbox,
  ClipboardList,
  Megaphone,
  Mail,
  PieChart,
  BriefcaseMedical,
  Settings,
  FileText,
  CalendarCheck,
  CalendarDays,
  UsersRound,
  HeartHandshake,
  ScrollText,
  Phone,
  Warehouse,
  Stethoscope,
  Wallet,
  Package,
  ChevronRight,
  ChevronDown,
  SlidersHorizontal,
  Upload,
  Download,
  LogOut,
  UserRound,
  SquareTerminal,
  ShieldCheck
} from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import {
  BottomNavBar,
  BottomNavItem,
  MoreSheetOverlay,
  MoreSheet,
  MoreSheetHandle,
  MoreSheetList,
  MoreSheetItem,
  MoreSheetSubList,
  MoreSheetSubItem
} from './styled';

const ICON_PROPS = { size: 22, strokeWidth: 1.75 };
const SHEET_ICON_PROPS = { size: 20, strokeWidth: 1.75 };
const SUB_ICON_PROPS = { size: 18, strokeWidth: 1.75 };

// Secondary destinations shown in the "More" bottom sheet. Items with a
// `children` array expand inline (accordion) to reveal their sub-pages.
const MORE_ITEMS = [
  { key: ROUTES.ADMIN_PULSE, label: 'Pulse', Icon: Heart, permission: 'analytics' },
  { key: ROUTES.ADMIN_INBOX, label: 'Inbox', Icon: Inbox, permission: 'messaging' },
  { key: ROUTES.ADMIN_SURVEYS, label: 'Surveys', Icon: ClipboardList, adminOnly: true },
  { key: ROUTES.ADMIN_CASE_MANAGEMENT, label: 'Outreach', Icon: Megaphone, permission: 'case_management' },
  { key: ROUTES.ADMIN_EMAIL_CAMPAIGNS, label: 'Email Campaigns', Icon: Mail, adminOnly: true },
  {
    key: 'analytics',
    label: 'Analytics',
    Icon: PieChart,
    children: [
      { key: ROUTES.ADMIN_ANALYTICS_REPORTS, label: 'Reports', Icon: FileText, permission: 'analytics' },
      { key: ROUTES.ADMIN_ANALYTICS_HEALTH_OUTCOMES, label: 'Health Outcomes', Icon: Heart, permission: 'analytics' },
      { key: ROUTES.ADMIN_INSURANCE_ENROLLMENTS, label: 'Insurance', Icon: ShieldCheck, permission: 'insurance_enrollment' },
      { key: ROUTES.ADMIN_ANALYTICS_OPERATIONS, label: 'Operations', Icon: Settings, permission: 'analytics' },
      { key: ROUTES.ADMIN_ANALYTICS_EVENTS, label: 'Events', Icon: CalendarCheck, permission: 'analytics' },
      { key: ROUTES.ADMIN_ANALYTICS_BOOKING, label: 'Booking', Icon: CalendarDays, permission: 'analytics' },
      { key: ROUTES.ADMIN_ANALYTICS_POPULATION, label: 'Population', Icon: UsersRound, permission: 'analytics' },
      { key: ROUTES.ADMIN_ANALYTICS_VOLUNTEERS, label: 'Volunteers', Icon: HeartHandshake, permission: 'analytics' },
      { key: ROUTES.ADMIN_AUDIT_LOGS, label: 'Logs', Icon: ScrollText, permission: 'audit_logs' },
      { key: ROUTES.ADMIN_CALL_LOGS, label: 'Call Logs', Icon: Phone, permission: 'voice_calls' }
    ]
  },
  {
    key: 'ondemand',
    label: 'Deployed Hearts',
    Icon: BriefcaseMedical,
    children: [
      { key: ROUTES.ADMIN_ONDEMAND_STORAGE, label: 'Storage Units', Icon: Warehouse, permission: 'ondemand_management' },
      { key: ROUTES.ADMIN_ONDEMAND_HCWS, label: 'Healthcare Workers', Icon: Stethoscope, permission: 'ondemand_management' },
      { key: ROUTES.ADMIN_ONDEMAND_EVENTS, label: 'Events', Icon: CalendarCheck, permission: 'ondemand_management' },
      { key: ROUTES.ADMIN_ONDEMAND_PATIENTS, label: 'Patients', Icon: Users, permission: 'ondemand_management' },
      { key: ROUTES.ADMIN_ONDEMAND_PAYOUTS, label: 'Payouts', Icon: Wallet, permission: 'ondemand_management' },
      { key: ROUTES.ADMIN_ONDEMAND_ORDERS, label: 'Orders', Icon: Package, permission: 'ondemand_management' }
    ]
  },
  { key: ROUTES.SETTINGS, label: 'Settings', Icon: Settings, adminOnly: true }
];

const PRIMARY_TAB_KEYS = [
  ROUTES.ADMIN_DASHBOARD,
  ROUTES.ADMIN_EVENTS,
  ROUTES.ADMIN_PATIENT_QUEUE,
  ROUTES.ADMIN_ALL_MEMBERS
];

const MobileBottomNav = ({
  selectedKeys = [],
  intakeCount = 0,
  userName = 'Admin User',
  canManageUsers = false,
  isAdmin = false,
  hasPermission = () => false,
  canInstall = false,
  onInstall,
  onLogout
}) => {
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Mirrors the desktop sidebar: only show pages this user can open.
  const canSee = (item) => {
    if (isAdmin) return true;
    if (item.adminOnly) return false;
    return !item.permission || hasPermission(item.permission);
  };
  const moreItems = MORE_ITEMS
    .map((item) => (item.children ? { ...item, children: item.children.filter(canSee) } : item))
    .filter((item) => canSee(item) && (!item.children || item.children.length > 0));
  const [expandedKey, setExpandedKey] = useState(null);

  const activeKey = selectedKeys[0];
  // "More" is highlighted whenever the current page isn't one of the four
  // primary tabs (i.e. it lives inside the More sheet).
  const moreActive = !PRIMARY_TAB_KEYS.includes(activeKey);

  const primaryTabs = [
    { key: ROUTES.ADMIN_DASHBOARD, label: 'Home', Icon: Home, permission: 'dashboard' },
    { key: ROUTES.ADMIN_EVENTS, label: 'Bookings', Icon: Calendar, permission: 'event_management' },
    { key: ROUTES.ADMIN_PATIENT_QUEUE, label: 'Intake', Icon: Clock, badge: intakeCount, permission: 'patient_queue' },
    { key: ROUTES.ADMIN_ALL_MEMBERS, label: 'Members', Icon: Users, permission: 'screening_data' }
  ].filter(canSee);

  const closeSheet = () => {
    setSheetOpen(false);
    setExpandedKey(null);
  };

  const go = (key) => {
    navigate(key);
    closeSheet();
  };

  const handleMoreItem = (item) => {
    if (item.children) {
      setExpandedKey((prev) => (prev === item.key ? null : item.key));
    } else {
      go(item.key);
    }
  };

  // Account actions mirroring the desktop profile menu (bottom-left).
  const accountItems = [
    isAdmin && { key: ROUTES.ADMIN_FORM_CONFIG, label: 'Form Config', Icon: SlidersHorizontal, action: 'nav' },
    isAdmin && { key: ROUTES.ADMIN_DATA_IMPORT, label: 'Data Import', Icon: Upload, action: 'nav' },
    isAdmin && { key: ROUTES.ADMIN_BACKEND_LOGS, label: 'Backend Logs', Icon: SquareTerminal, action: 'nav' },
    canManageUsers && { key: ROUTES.ADMIN_USERS, label: 'Users', Icon: UsersRound, action: 'nav' },
    canInstall && { key: 'install', label: 'Install App', Icon: Download, action: 'install' },
    { key: 'logout', label: 'Logout', Icon: LogOut, action: 'logout' }
  ].filter(Boolean);

  const handleAccountItem = (item) => {
    if (item.action === 'logout') {
      closeSheet();
      onLogout?.();
    } else if (item.action === 'install') {
      closeSheet();
      onInstall?.();
    } else {
      go(item.key);
    }
  };

  return (
    <>
      <BottomNavBar>
        {primaryTabs.map(({ key, label, Icon, badge }) => (
          <BottomNavItem
            key={key}
            type="button"
            $active={activeKey === key}
            onClick={() => navigate(key)}
            aria-label={label}
          >
            {badge > 0 ? (
              <Badge
                count={badge}
                size="small"
                overflowCount={99}
                offset={[6, -2]}
                style={{ boxShadow: 'none' }}
              >
                <Icon {...ICON_PROPS} />
              </Badge>
            ) : (
              <Icon {...ICON_PROPS} />
            )}
            <span>{label}</span>
          </BottomNavItem>
        ))}
        <BottomNavItem
          type="button"
          $active={moreActive}
          onClick={() => setSheetOpen(true)}
          aria-label="More"
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
        >
          <MenuIcon {...ICON_PROPS} />
          <span>More</span>
        </BottomNavItem>
      </BottomNavBar>

      <MoreSheetOverlay $open={sheetOpen} onClick={closeSheet} />
      <MoreSheet $open={sheetOpen} role="dialog" aria-modal="true" aria-label="More navigation">
        <MoreSheetHandle onClick={closeSheet} />
        <MoreSheetList>
          {moreItems.map((item) => {
            const { key, label, Icon, children } = item;
            const isExpanded = expandedKey === key;
            const isActive = children
              ? children.some((c) => c.key === activeKey)
              : activeKey === key;
            return (
              <React.Fragment key={key}>
                <MoreSheetItem
                  type="button"
                  $active={isActive}
                  onClick={() => handleMoreItem(item)}
                >
                  <span className="left">
                    <Icon {...SHEET_ICON_PROPS} />
                    <span className="label">{label}</span>
                  </span>
                  {children ? (
                    isExpanded ? <ChevronDown {...SUB_ICON_PROPS} /> : <ChevronRight {...SUB_ICON_PROPS} />
                  ) : (
                    <ChevronRight {...SUB_ICON_PROPS} />
                  )}
                </MoreSheetItem>
                {children && (
                  <MoreSheetSubList $open={isExpanded}>
                    {children.map((child) => (
                      <MoreSheetSubItem
                        key={child.key}
                        type="button"
                        $active={activeKey === child.key}
                        onClick={() => go(child.key)}
                      >
                        <child.Icon {...SUB_ICON_PROPS} />
                        <span className="label">{child.label}</span>
                      </MoreSheetSubItem>
                    ))}
                  </MoreSheetSubList>
                )}
              </React.Fragment>
            );
          })}
          <MoreSheetItem
            type="button"
            $active={expandedKey === 'account'}
            onClick={() => setExpandedKey((prev) => (prev === 'account' ? null : 'account'))}
          >
            <span className="left">
              <UserRound {...SHEET_ICON_PROPS} />
              <span className="label">Account</span>
            </span>
            {expandedKey === 'account' ? <ChevronDown {...SUB_ICON_PROPS} /> : <ChevronRight {...SUB_ICON_PROPS} />}
          </MoreSheetItem>
          <MoreSheetSubList $open={expandedKey === 'account'}>
            <div
              style={{
                padding: '8px 16px 6px',
                fontSize: 12,
                fontWeight: 600,
                color: '#8c8c8c'
              }}
            >
              {userName}
            </div>
            {accountItems.map((item) => (
              <MoreSheetSubItem
                key={item.key}
                type="button"
                $active={item.action === 'nav' && activeKey === item.key}
                onClick={() => handleAccountItem(item)}
                style={item.action === 'logout' ? { color: '#e11d48' } : undefined}
              >
                <item.Icon {...SUB_ICON_PROPS} />
                <span className="label">{item.label}</span>
              </MoreSheetSubItem>
            ))}
          </MoreSheetSubList>
        </MoreSheetList>
      </MoreSheet>
    </>
  );
};

export default MobileBottomNav;
